import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import type { Router } from '@tanstack/react-router';
import { clientLogger } from '@/lib/clientLogger';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';

interface CaptureContext {
  component?: string;
  action?: string;
  [key: string]: unknown;
}

// Loki gets one short line per crash so the product-health dashboard shows client
// failures next to server ones. The stack stays in Sentry; Sentry's dedupe
// integration already collapses a render loop throwing the same error.
export function crashLogData(event: ErrorEvent): Record<string, unknown> {
  const exception = event.exception?.values?.[0];
  return {
    errorName: exception?.type ?? 'Error',
    errorMessage: (exception?.value ?? event.message ?? '').slice(0, 200),
    mechanism: exception?.mechanism?.type,
    component: event.tags?.component,
    action: event.tags?.action,
  };
}

export function initSentry(): void {
  if (!SENTRY_DSN || Sentry.getClient()) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    beforeSend(event) {
      clientLogger.error('client.crash', crashLogData(event));
      return event;
    },
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
    replaysSessionSampleRate: import.meta.env.DEV ? 0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
  });
}

// Wire up TanStack Router tracing after the router is created.
// Called from router.tsx where the instance is available.
export function initSentryRouterTracing(router: Router<never, never>): void {
  if (!SENTRY_DSN || !Sentry.getClient()) return;
  Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router as never));
}

export function setSentryUser(user: { id: string; email?: string; name?: string } | null): void {
  if (!SENTRY_DSN) return;
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email, username: user.name });
  } else {
    Sentry.setUser(null);
  }
}

export function captureException(error: unknown, context: CaptureContext = {}): void {
  if (!SENTRY_DSN) return;
  Sentry.captureException(error, {
    tags: { component: context.component, action: context.action },
    extra: context,
  });
}

export function getSentryReplayId(): string | undefined {
  if (!SENTRY_DSN) return undefined;
  return Sentry.getReplay()?.getReplayId();
}
